import { useRef, useState } from 'react';
import {
  Person as PersonIcon,
  CameraAlt as CameraAltIcon,
} from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { NotificationSeverity } from '@/components/notifications/Notification';
import { useNotification } from '@/hooks';
import { useUploadDriverProfilePictureMutation } from '../services';
import ImageLightbox from './ImageLightbox';

type EditableDriverProfilePictureProps = {
  driverId: string;
  fullName: string;
  profilePictureUrl: string | null;
};

const EditableDriverProfilePicture = ({
  driverId,
  fullName,
  profilePictureUrl,
}: EditableDriverProfilePictureProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showNotification } = useNotification();
  const [uploadProfilePicture, { isLoading: isUploading }] =
    useUploadDriverProfilePictureMutation();

  const imageAlt = `Foto de perfil de ${fullName || 'conductor'}`;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      await uploadProfilePicture({ id: driverId, formData }).unwrap();
      showNotification(
        'Foto actualizada correctamente',
        NotificationSeverity.success
      );
    } catch {
      showNotification(
        'No se pudo actualizar la foto. Intentá de nuevo.',
        NotificationSeverity.error
      );
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className='relative w-28 h-28 flex-shrink-0'>
      <button
        type='button'
        onClick={() =>
          profilePictureUrl && !isUploading && setLightboxOpen(true)
        }
        disabled={!profilePictureUrl || isUploading}
        className='w-full h-full rounded-2xl overflow-hidden bg-gray-100 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 disabled:cursor-default cursor-pointer shadow-sm transition-shadow hover:shadow-md'
        aria-label={profilePictureUrl ? 'Ver foto en grande' : undefined}
      >
        {isUploading ? (
          <CircularProgress size={36} sx={{ color: '#9ca3af' }} />
        ) : profilePictureUrl ? (
          <img
            src={profilePictureUrl}
            alt={imageAlt}
            className='w-full h-full object-cover'
            referrerPolicy='no-referrer'
          />
        ) : (
          <PersonIcon sx={{ fontSize: 56 }} className='text-gray-400' />
        )}
      </button>

      <button
        type='button'
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className='absolute bottom-1 right-1 w-8 h-8 rounded-full bg-blue-600 text-white shadow-md flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400'
        aria-label='Cambiar foto de perfil'
      >
        <CameraAltIcon sx={{ fontSize: 16 }} />
      </button>

      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        className='hidden'
        onChange={handleFileChange}
      />

      {profilePictureUrl && (
        <ImageLightbox
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          imageUrl={profilePictureUrl}
          alt={imageAlt}
        />
      )}
    </div>
  );
};

export default EditableDriverProfilePicture;
